import { Box, Drawer, Typography } from "@mui/material";
import { DataTable } from "@/components/DesignSystem";
import type { AttackerSpend, AttackerSpendTier } from "@/types";

function sek(n: number): string {
  return `${Math.round(n).toLocaleString("sv-SE")} kr`;
}

// The persistence signal is only meaningful when several IPs recurred; below
// this (or when the API doesn't send the field) the line is hidden.
const MULTIDAY_SIGNAL_MIN = 2;

const TIER_COLUMNS = [
  { header: "Tier", accessor: (t: AttackerSpendTier) => t.label },
  { header: "IPs", accessor: (t: AttackerSpendTier) => t.ip_count.toLocaleString("sv-SE"), align: "right" as const },
  { header: "Blocked", accessor: (t: AttackerSpendTier) => t.attempt_count.toLocaleString("sv-SE"), align: "right" as const },
  { header: "Est. data", accessor: (t: AttackerSpendTier) => `${t.est_gb} GB`, align: "right" as const },
  { header: "Basis", accessor: (t: AttackerSpendTier) => t.price_basis },
  { header: "Spend", accessor: (t: AttackerSpendTier) => sek(t.spend_sek), align: "right" as const },
];

function InfrastructureSection({
  infra,
  multidayIpCount,
}: {
  readonly infra: AttackerSpend["infrastructure"];
  readonly multidayIpCount: number;
}) {
  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
        Infrastructure pointed at you
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        ~{infra.total_devices.toLocaleString("sv-SE")} distinct devices across{" "}
        {infra.distinct_asns.toLocaleString("sv-SE")} networks (
        {infra.datacenter_ips.toLocaleString("sv-SE")} datacenter,{" "}
        {infra.residential_devices.toLocaleString("sv-SE")} residential/ISP). Renting infrastructure
        at that scale costs roughly {sek(infra.rental_value_sek_per_week)}/week — a shared asset aimed
        at many targets, not just you.
      </Typography>
      {multidayIpCount >= MULTIDAY_SIGNAL_MIN && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {multidayIpCount.toLocaleString("sv-SE")} IPs were held across multiple days — sustained
          infrastructure.
        </Typography>
      )}
    </>
  );
}

function sophisticationNote(p: number): string {
  if (p >= 0.66)
    return "each IP used only briefly across many networks (premium residential rotation), priced near the top of the band.";
  if (p <= 0.33)
    return "a few IPs reused heavily across relatively few networks (cheaper, bulk-style infrastructure), priced near the bottom of the band.";
  return "a mix of reuse and spread, priced around the middle of the band.";
}

function MethodologySection({ a }: { readonly a: AttackerSpend["assumptions"] }) {
  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
        How we calculate this
      </Typography>
      <Typography variant="body2" component="div" sx={{ color: "text.secondary" }}>
        <Box component="ul" sx={{ m: 0, pl: "1.1rem" }}>
          <li>Base: every IP Shield blocked with no successful login over the period.</li>
          <li>
            Classified from network type: hosting networks → datacenter; everything else (residential,
            ISP, mobile) → the residential / ISP tier.
          </li>
          <li>
            Headline is the market cost to deliver this traffic (per-GB bandwidth). Datacenter priced
            at datacenter rates; residential priced at the residential market, per GB.
          </li>
          <li>
            Residential pricing tracks the attack's shape — ~{Math.round(a.mean_attempts_per_ip)}{" "}
            attempts/IP across {a.asn_count.toLocaleString("sv-SE")} residential networks:{" "}
            {sophisticationNote(a.sophistication_position)}
          </li>
          <li>
            Assumes {Math.round(a.bytes_per_attempt / 1000)} KB per attempt (band{" "}
            {a.bytes_per_attempt_low / 1000}–{a.bytes_per_attempt_high / 1000} KB); FX {a.sek_per_usd}{" "}
            SEK/USD; prices as of {a.prices_as_of}.
          </li>
          <li>Conservative: counts blocked attempts only — credentials, CAPTCHA and tooling excluded.</li>
        </Box>
      </Typography>
    </>
  );
}

export default function AttackerSpendDrawer({
  open,
  onClose,
  data,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly data: AttackerSpend | null;
}) {
  if (!data) return null;
  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: "100%", sm: 600 } } } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Estimated attacker spend
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Your attackers spent an estimated <strong>{sek(data.central_sek)}</strong> over the last{" "}
          {data.period_days} days to deliver this traffic — and 0 logins succeeded. Range{" "}
          {sek(data.low_sek)}–{sek(data.high_sek)}.
        </Typography>

        <DataTable columns={TIER_COLUMNS} rows={data.tiers} getRowKey={(t) => t.tier} />

        {data.tiers.map((t) => (
          <Box key={`asns-${t.tier}`} sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t.label} — top networks:{" "}
              {t.representative_asns.map((n) => `${n.name} (${n.asn})`).join(", ") || "—"}
            </Typography>
          </Box>
        ))}

        <InfrastructureSection infra={data.infrastructure} multidayIpCount={data.multiday_ip_count ?? 0} />
        <MethodologySection a={data.assumptions} />
      </Box>
    </Drawer>
  );
}
